import React from 'react';
import { Link } from 'react-router-dom';
import { css, cx } from '@emotion/css';

// ─── Category → subtitle tag line ────────────────────────────────────────────
const getCategorySubtitle = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('math') || n.includes('algebra')) return 'FROM BASICS TO MASTERY';
  if (n.includes('science') || n.includes('bio') || n.includes('chem')) return 'EXPLORE THE NATURAL WORLD';
  if (n.includes('physics')) return 'LAWS THAT GOVERN EVERYTHING';
  if (n.includes('history')) return 'LESSONS FROM THE PAST';
  if (n.includes('geo')) return 'DISCOVER EVERY CORNER';
  if (n.includes('tech') || n.includes('computer')) return 'BLUEPRINT TO FUTURE-PROOF';
  if (n.includes('business') || n.includes('economics')) return 'PROBLEM SOLVER TO PAYING CUSTOMER';
  if (n.includes('sport')) return 'PUSH YOUR LIMITS';
  if (n.includes('english') || n.includes('grammar')) return 'MASTER THE LANGUAGE';
  if (n.includes('literature') || n.includes('book')) return 'STORIES THAT SHAPE MINDS';
  if (n.includes('spell')) return 'WORDS THAT MATTER';
  if (n.includes('art')) return 'CREATIVITY UNLEASHED';
  if (n.includes('music')) return 'FEEL THE RHYTHM';
  return 'CLASSROOM MISSION';
};

// ─── Category → icon emoji ────────────────────────────────────────────────────
const getCategoryIcon = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('math') || n.includes('algebra')) return '📐';
  if (n.includes('bio')) return '🧬';
  if (n.includes('chem')) return '⚗️';
  if (n.includes('physics')) return '⚛️';
  if (n.includes('science')) return '🔬';
  if (n.includes('history')) return '🏛️';
  if (n.includes('geo')) return '🌍';
  if (n.includes('tech') || n.includes('computer') || n.includes('coding')) return '💻';
  if (n.includes('business') || n.includes('economics')) return '💼';
  if (n.includes('sport')) return '🏆';
  if (n.includes('english') || n.includes('grammar')) return '📖';
  if (n.includes('literature') || n.includes('book')) return '📚';
  if (n.includes('spell')) return '✍️';
  if (n.includes('art')) return '🎨';
  if (n.includes('music')) return '🎵';
  return '🎯';
};

// ─── Category → short description ────────────────────────────────────────────
const getCategoryDescription = (name, count) => {
  const n = (name || '').toLowerCase();
  if (n.includes('math') || n.includes('algebra'))
    return `Sharpen your skills with ${count}+ number challenges, from arithmetic to advanced concepts.`;
  if (n.includes('science') || n.includes('bio') || n.includes('chem'))
    return `Dive deep into the world of science with ${count}+ questions spanning every discipline.`;
  if (n.includes('physics'))
    return `Unravel the laws of the universe through ${count}+ physics challenges.`;
  if (n.includes('history'))
    return `Travel through time with ${count}+ questions on world events and civilisations.`;
  if (n.includes('geo'))
    return `Explore continents, capitals, and cultures through ${count}+ geography challenges.`;
  if (n.includes('tech') || n.includes('computer'))
    return `Test your tech know-how with ${count}+ cutting-edge questions for the modern learner.`;
  if (n.includes('business') || n.includes('economics'))
    return `The track dedicated to survival, growth, and sustainable knowledge generation.`;
  if (n.includes('sport'))
    return `Compete and conquer with ${count}+ sports trivia and fitness challenges.`;
  if (n.includes('english') || n.includes('grammar'))
    return `Master the English language with ${count}+ grammar, vocab, and comprehension challenges.`;
  if (n.includes('spell'))
    return `${count}+ spelling challenges to sharpen your word power and vocabulary.`;
  return `${count}+ curated questions to challenge and expand your knowledge.`;
};

// ─── Styles matching PracticeNewCard ─────────────────────────────────────────
const cardStyles = css`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 400px;
  border-radius: 28px;
  overflow: hidden;
  text-decoration: none;
  box-shadow: 0px 10px 30px rgba(241, 102, 2, 0.05);
  border: 1.5px solid rgba(241, 102, 2, 0.18);
  background: linear-gradient(to bottom, #fffcf9 0%, #fff3e6 65%, #ffe6cc 100%);
  transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 25px 50px rgba(241, 102, 2, 0.15), 0 0 35px rgba(241, 102, 2, 0.08);
    text-decoration: none;
    border-color: rgba(241, 102, 2, 0.45);
    background: linear-gradient(to bottom, #fffcf9 0%, #fff0e0 55%, #ffd4a8 100%);

    .card-logo-container {
      border-color: #f16602;
      box-shadow: 0 15px 40px rgba(241, 102, 2, 0.2);
      transform: scale(1.05);
      
      img {
        transform: scale(1.1);
      }
    }

    .logo-glow {
      transform: scale(1.2);
      opacity: 0.8;
    }
  }

  .card-logo-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 1.5rem;
    margin-bottom: 0.8rem;
    position: relative;
  }

  .card-logo-container {
    width: 140px;
    height: 140px;
    border-radius: 32px;
    overflow: hidden;
    background: #ffffff;
    border: 2px solid rgba(241, 102, 2, 0.18);
    box-shadow: 0 10px 35px rgba(241, 102, 2, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1);
    z-index: 2;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      padding: 12px;
      transition: transform 0.6s cubic-bezier(0.25, 1, 0.5, 1);
    }
  }

  .logo-glow {
    position: absolute;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(241, 102, 2, 0.15) 0%, transparent 70%);
    filter: blur(8px);
    z-index: 1;
    pointer-events: none;
    transition: all 0.4s ease;
  }

  .card-icon-container {
    position: absolute;
    top: 1.2rem;
    left: 1.2rem;
    z-index: 3;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(241, 102, 2, 0.18);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
  }

  .card-badge-container {
    position: absolute;
    top: 1.2rem;
    right: 1.2rem;
    z-index: 3;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(241, 102, 2, 0.18);
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 0.68rem;
    font-weight: 700;
    color: #f16602;
    letter-spacing: 0.02em;
    display: flex;
    align-items: center;
    gap: 6px;

    .badge-dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: #f16602;
      box-shadow: 0 0 8px #f16602;
    }
  }

  .card-content-area {
    padding: 0 2rem 1.5rem 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .card-subtitle {
    font-size: 0.68rem;
    font-weight: 700;
    color: #f16602;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin-bottom: 0.4rem;
  }

  .card-title {
    font-size: 1.55rem;
    font-weight: 800;
    color: #382110;
    text-transform: uppercase;
    letter-spacing: -0.01em;
    margin: 0 0 0.5rem 0;
    line-height: 1.25;
  }

  .card-description {
    font-size: 0.84rem;
    color: #594436;
    line-height: 1.45;
    margin: 0 0 0.8rem 0;
    font-weight: 500;
  }

  .btn-primary {
    cursor: pointer;
    padding: 0.8rem;
    width: 100%;
    margin-top: auto;
    background: #f16602;
    font-size: 0.8rem;
    font-weight: 700;
    color: #ffffff;
    border: 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-radius: 9999px;
    box-shadow: 0 4px 15px rgba(241, 102, 2, 0.25);
    transition: all 0.3s ease;
    text-align: center;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(241, 102, 2, 0.35);
    }
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────
function EventNewCard({ title, levels, totalquestions, event, imgsrc }) {
  const subtitle = getCategorySubtitle(title);
  const icon = getCategoryIcon(title);
  const description = getCategoryDescription(title, totalquestions);

  return (
    <Link to={{ pathname: '/quiz', state: event }} className={cx(cardStyles)}>
      {/* Top Left Floating Icon */}
      <div className="card-icon-container">
        {icon}
      </div>

      {/* Top Right Floating Badge */}
      <div className="card-badge-container">
        <span className="badge-dot" />
        <span>{totalquestions === 0 ? 'NO' : totalquestions} QUESTIONS</span>
      </div>

      {/* Center Logo Container with glow */}
      <div className="card-logo-wrapper">
        <div className="card-logo-container">
          <img src={imgsrc} alt={title} onError={(e) => e.target.src = 'https://api.quizwizard.net/assets/MathBowl.png'} />
        </div>
        <div className="logo-glow" />
      </div>

      {/* Text Content */}
      <div className="card-content-area">
        <span className="card-subtitle">{subtitle}</span>
        <h3 className="card-title">{title}</h3>
        <p className="card-description">{description}</p>
        
        {/* Start Button */}
        <button className="btn-primary">
          Start Mission
        </button>
      </div>
    </Link>
  );
}

export default EventNewCard;
